import { useState } from "react";
import { Lock, Mail, ShieldCheck, KeyRound } from "lucide-react";
import { adminLogin, adminMfaSetup, adminMfaVerify, adminChangePassword, apiError } from "../../lib/api";
import { LOGO_URL, ORG } from "../../lib/constants";

export const adminFieldCls =
  "w-full bg-white border border-brand-line rounded-xl px-4 py-3 text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:border-brand-teal transition-colors";

function Shell({ title, subtitle, children, onSubmit, testid }) {
  return (
    <main className="min-h-screen flex items-center justify-center bg-brand-cream px-4 py-10">
      <form onSubmit={onSubmit} noValidate data-testid={testid}
        className="bg-white border border-brand-line rounded-3xl p-8 md:p-10 w-full max-w-md shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <img src={LOGO_URL} alt="" className="h-12 w-12 object-contain" />
          <div>
            <h1 className="font-serif text-xl text-brand-charcoal">{title}</h1>
            <p className="text-xs text-brand-muted">{subtitle || ORG.name}</p>
          </div>
        </div>
        {children}
      </form>
    </main>
  );
}

function ErrorText({ error }) {
  return (
    <p role="alert" aria-live="assertive" className="mt-3 text-sm text-red-700 min-h-[1.25rem]" data-testid="admin-auth-error">
      {error}
    </p>
  );
}

function SubmitBtn({ loading, label, testid }) {
  return (
    <button type="submit" disabled={loading} data-testid={testid}
      className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-brand-teal text-white rounded-full px-6 py-3 font-medium hover:bg-brand-tealDark transition-colors disabled:opacity-60">
      {loading ? "Please wait…" : label}
    </button>
  );
}

function IconInput({ id, label, icon: Icon, ...props }) {
  return (
    <div className="mt-4">
      <label htmlFor={id} className="block text-xs font-medium text-brand-muted mb-1.5 tracking-wide">{label}</label>
      <div className="relative">
        <Icon aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted" />
        <input id={id} className={adminFieldCls + " pl-10"} {...props} />
      </div>
    </div>
  );
}

function CodeStep({ mfaToken, setup, onAuthed }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const r = await adminMfaVerify(mfaToken, code);
      onAuthed(r.admin);
    } catch (err) { setError(apiError(err, "Verification failed.")); } finally { setLoading(false); }
  };
  return (
    <Shell title={setup ? "Set up two-step verification" : "Two-step verification"} onSubmit={submit} testid="admin-mfa-form">
      {setup && (
        <div className="text-sm text-brand-charcoal space-y-3" data-testid="admin-mfa-setup">
          <p>Scan this QR code with an authenticator app (Google Authenticator, Microsoft Authenticator, or Authy).</p>
          <img src={setup.qr_data_url} alt="QR code for your authenticator app" className="w-44 h-44 mx-auto border border-brand-line rounded-xl" />
          <p className="text-xs text-brand-muted">Can't scan? Enter this key manually:</p>
          <code className="block text-xs break-all bg-brand-cream rounded-lg px-3 py-2" data-testid="admin-mfa-secret">{setup.secret}</code>
        </div>
      )}
      <IconInput id="admin-mfa-code" label="6-digit code from your authenticator app" icon={ShieldCheck}
        inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus required
        value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="admin-mfa-code" />
      <ErrorText error={error} />
      <SubmitBtn loading={loading} label="Verify" testid="admin-mfa-submit" />
    </Shell>
  );
}

export function AdminLogin({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const r = await adminLogin(email, password);
      const setup = r.mfa_enrolled ? null : await adminMfaSetup(r.mfa_token);
      setPassword("");
      setMfa({ token: r.mfa_token, setup });
    } catch (err) { setError(apiError(err, "Sign in failed.")); } finally { setLoading(false); }
  };

  if (mfa) return <CodeStep mfaToken={mfa.token} setup={mfa.setup} onAuthed={onAuthed} />;
  return (
    <Shell title="Admin Portal" onSubmit={submit} testid="admin-login-form">
      <IconInput id="admin-email" label="Email" icon={Mail} type="email" autoComplete="username" autoFocus required
        value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
      <IconInput id="admin-password" label="Password" icon={Lock} type="password" autoComplete="current-password" required
        value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
      <ErrorText error={error} />
      <SubmitBtn loading={loading} label="Continue" testid="admin-login-btn" />
      <p className="mt-5 text-xs text-brand-muted text-center">Restricted to authorized Hosea Ministries staff. All access is logged.</p>
    </Shell>
  );
}

export function ChangePassword({ onDone }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) return setError("New passwords do not match.");
    setError(""); setLoading(true);
    try { await adminChangePassword(cur, next); onDone(); }
    catch (err) { setError(apiError(err, "Could not change password.")); } finally { setLoading(false); }
  };
  return (
    <Shell title="Choose a new password" subtitle="Required before you continue" onSubmit={submit} testid="admin-change-password-form">
      <p className="text-sm text-brand-muted">Use at least 12 characters with letters and numbers.</p>
      <IconInput id="pw-current" label="Current (temporary) password" icon={KeyRound} type="password" autoComplete="current-password"
        value={cur} onChange={(e) => setCur(e.target.value)} data-testid="admin-current-password" />
      <IconInput id="pw-new" label="New password" icon={Lock} type="password" autoComplete="new-password"
        value={next} onChange={(e) => setNext(e.target.value)} data-testid="admin-new-password" />
      <IconInput id="pw-confirm" label="Confirm new password" icon={Lock} type="password" autoComplete="new-password"
        value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="admin-confirm-password" />
      <ErrorText error={error} />
      <SubmitBtn loading={loading} label="Save password" testid="admin-change-password-submit" />
    </Shell>
  );
}
